import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import * as L from 'leaflet';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import { TranslateService } from '@ngx-translate/core';

@Component({
  standalone: false,
  selector: 'app-location-search-popup',
  templateUrl: './location-search-popup.component.html',
  styleUrl: './location-search-popup.component.scss',
})
export class LocationSearchPopupComponent implements AfterViewInit, OnDestroy {
  @Input() latitude: number | null = null;
  @Input() longitude: number | null = null;
  @Input() onConfirm!: (lat: number, lng: number) => void;

  mymap: L.Map;
  marker: L.Marker;
  selectedLatitude: number;
  selectedLongitude: number;

  constructor(
    public bsModalRef: BsModalRef,
    private translateService: TranslateService,
  ) {}

  ngAfterViewInit(): void {
    if (this.latitude && this.longitude) {
      this.selectedLatitude = this.latitude;
      this.selectedLongitude = this.longitude;
      this.setupMap();
    } else {
      this.useMyLocation();
    }
  }

  ngOnDestroy(): void {
    if (this.mymap) {
      this.mymap.off();
      this.mymap.remove();
    }
  }

  setupMap() {
    if (this.mymap) {
      this.mymap.remove();
    }

    this.mymap = L.map('search-location-map', {
      center: [this.selectedLatitude, this.selectedLongitude],
      zoom: 15,
      renderer: L.canvas(),
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      className: 'map-tiles',
    }).addTo(this.mymap);

    const searchForAddress =
      this.translateService.instant('search-for-address');

    const provider = new OpenStreetMapProvider();
    const searchControl = GeoSearchControl({
      provider: provider,
      style: 'bar',
      showMarker: false,
      showPopup: false,
      autoClose: true,
      retainZoomLevel: false,
      animateZoom: true,
      keepResult: true,
      searchLabel: searchForAddress,
    });
    this.mymap.addControl(searchControl);

    const markerIcon = L.divIcon({
      html: `<svg class="tw-w-full tw-h-full tw-fill-primary tw-drop-shadow" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.9135 17.5854L17.6771 18.8083C16.7659 19.7021 15.5833 20.8521 14.1291 22.2583C13.6921 22.6809 13.1079 22.9171 12.5 22.9171C11.892 22.9171 11.3079 22.6809 10.8708 22.2583L7.23435 18.7208C6.77671 18.2722 6.39407 17.8937 6.08643 17.5854C4.81806 16.317 3.95431 14.7009 3.60441 12.9415C3.2545 11.1822 3.43415 9.35857 4.12064 7.70132C4.80713 6.04407 5.96963 4.6276 7.46113 3.63103C8.95264 2.63446 10.7062 2.10254 12.5 2.10254C14.2938 2.10254 16.0473 2.63446 17.5388 3.63103C19.0303 4.6276 20.1928 6.04407 20.8793 7.70132C21.5658 9.35857 21.7454 11.1822 21.3955 12.9415C21.0456 14.7009 20.1819 16.317 18.9135 17.5854ZM15.1041 11.4583C15.1041 10.7677 14.8298 10.1053 14.3414 9.6169C13.853 9.12853 13.1906 8.85416 12.5 8.85416C11.8093 8.85416 11.1469 9.12853 10.6585 9.6169C10.1702 10.1053 9.8958 10.7677 9.8958 11.4583C9.8958 12.149 10.1702 12.8114 10.6585 13.2998C11.1469 13.7881 11.8093 14.0625 12.5 14.0625C13.1906 14.0625 13.853 13.7881 14.3414 13.2998C14.8298 12.8114 15.1041 12.149 15.1041 11.4583Z"/>
            </svg>`,
      className: 'map-marker-icon',
      iconSize: [30, 30],
      iconAnchor: [15, 30],
    });

    this.marker = L.marker(
      [this.selectedLatitude, this.selectedLongitude],
      {
        icon: markerIcon,
        draggable: true,
      },
    ).addTo(this.mymap);

    this.mymap.on('click', (e: any) => {
      const latlng = e.latlng;
      this.setSelectedLocation(latlng.lat, latlng.lng);
    });

    this.marker.on('dragend', (e: any) => {
      const latlng = e.target.getLatLng();
      this.setSelectedLocation(latlng.lat, latlng.lng);
    });

    this.mymap.on('geosearch/showlocation', (e: any) => {
      const { location } = e;
      this.setSelectedLocation(location.y, location.x);
    });

    setTimeout(() => {
      this.mymap.invalidateSize();
    }, 100);
  }

  setSelectedLocation(lat: number, lng: number) {
    this.selectedLatitude = lat;
    this.selectedLongitude = lng;
    this.marker.setLatLng([lat, lng]);
  }

  useMyLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          this.selectedLatitude = position.coords.latitude;
          this.selectedLongitude = position.coords.longitude;
          this.setupMap();
        },
        (error: GeolocationPositionError) => {
          console.log('Geolocation error:', error);
          this.selectedLatitude = 42.6635;
          this.selectedLongitude = 21.1638;
          this.setupMap();
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    } else {
      console.log('Geolocation is not supported by this browser');
      this.selectedLatitude = 42.6635;
      this.selectedLongitude = 21.1638;
      this.setupMap();
    }
  }

  closeModal() {
    this.bsModalRef.hide();
  }

  confirm() {
    if (this.onConfirm && this.selectedLatitude && this.selectedLongitude) {
      this.onConfirm(this.selectedLatitude, this.selectedLongitude);
    }
    this.closeModal();
  }
}
